import React, { useState, useRef } from 'react';
import ReactPlayer from 'react-player';
import moment from 'moment';
import useImages from 'hooks/useGallery';
import { ui } from 'util/ui';
import ArchyModal from 'components/shell/ArchyModal';
import { PlayIcon, PauseIcon, DownloadIcon, CameraIcon } from 'components/shell/Icons';

/**
 * Preview player, UI refresh styling (Aaron, 5 Nov 2026): the shared pop-up frame, a black video
 * area, then a light control bar (play/pause, a slim progress bar, time, Download, Screenshot).
 * Screenshots download and are saved to the project's gallery (task #15).
 */

type Props = {
  video: IVideo;
  exit(): void;
  downloadVideo(): void;
  url: string;
  /** The project the video belongs to: screenshots are saved to its gallery (task #15). */
  projectID?: string;
};

type LoadType = {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
};

const toolBtn: React.CSSProperties = {
  height: 36,
  padding: '0 12px',
  border: `1px solid ${ui.control}`,
  borderRadius: 10,
  background: ui.surface,
  color: ui.text2,
  font: 'inherit',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
  flex: 'none',
};

const clock = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const two = (n: number) => (n < 10 ? `0${n}` : String(n));
  return h ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
};

export const VideoPlayer: React.FC<Props> = ({ video, exit, downloadVideo, url, projectID }) => {
  const player = useRef<ReactPlayer>(null);
  const { uploadImages } = useImages();
  const [shotNote, setShotNote] = useState('');
  const [playing, setPlaying] = useState(true);
  const [played, setPlayed] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [duration, setDuration] = useState(0);

  const screenShot = async () => {
    const videoPlayer = player.current ? (player.current.getInternalPlayer() as HTMLVideoElement) : null;
    if (!videoPlayer) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoPlayer.videoWidth;
    canvas.height = videoPlayer.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx!.drawImage(videoPlayer, 0, 0, canvas.width, canvas.height);
    const dataURI = canvas.toDataURL('image/jpeg');

    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    const blob = new Blob([bytes], { type: mimeString });
    const fileName = `${video.id} - screenshot - ${moment().format('YYYYMMDDHHmmss')}.jpg`;

    // Task #15: save the frame to the project's gallery as well as downloading it.
    if (projectID) {
      setShotNote('Saving to gallery…');
      const shot = new File([blob], fileName, { type: mimeString });
      uploadImages(shot, projectID, () => undefined)
        .then((id) => setShotNote(id ? 'Saved to gallery' : 'Couldn’t save to gallery (downloaded only)'))
        .catch(() => setShotNote('Couldn’t save to gallery (downloaded only)'));
    }

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const onProgress = (state: LoadType) => {
    setPlayed(state.played * 100);
    setLoaded(state.loaded * 100);
    setSeconds(state.playedSeconds);
    if (player.current) setDuration(player.current.getDuration() || 0);
  };

  const seek = (value: number) => {
    setPlayed(value);
    if (player.current) player.current.seekTo(value / 100);
  };

  return (
    <ArchyModal title={video.id} subtitle="Preview" onClose={exit} width={1000} media labelledBy="player-title">
      {/* Full width of the pop-up, 16:9, but never taller than the window allows; the video letterboxes inside. */}
      <div
        style={{
          background: '#000000',
          width: '100%',
          height: 'min(562px, calc((100vw - 32px) * 0.5625), calc(100vh - 220px))',
          display: 'flex',
        }}
      >
        <ReactPlayer
          url={url}
          playing={playing}
          width="100%"
          height="100%"
          onProgress={onProgress}
          onEnded={() => setPlaying(false)}
          ref={player}
          progressInterval={100}
          config={{ file: { attributes: { crossOrigin: 'anonymous' } } }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="archy-btn"
          aria-label={playing ? 'Pause' : 'Play'}
          title={playing ? 'Pause' : 'Play'}
          onClick={() => setPlaying(!playing)}
          style={{ width: 40, height: 40, flex: 'none', borderRadius: '50%', border: 'none', background: ui.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
        >
          {playing ? <PauseIcon size={16} color="#FFFFFF" /> : <PlayIcon size={18} color="#FFFFFF" />}
        </button>
        <div style={{ flex: '1 1 240px', position: 'relative', height: 20, display: 'flex', alignItems: 'center' }}>
          <div style={{ position: 'absolute', left: 0, right: 0, height: 6, borderRadius: 999, background: ui.line, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${loaded}%`, background: ui.control }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${played}%`, background: ui.accent }} />
          </div>
          <input
            type="range"
            aria-label="Position"
            min={0}
            max={100}
            step={0.1}
            value={played}
            onChange={(e) => seek(parseFloat(e.target.value))}
            className="archy-scrub"
            style={{ position: 'relative', width: '100%', margin: 0, padding: 0, border: 'none', outline: 'none', boxShadow: 'none', background: 'transparent' }}
          />
        </div>
        <span style={{ fontSize: 13, color: ui.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flex: 'none' }}>
          {clock(seconds)} / {clock(duration)}
        </span>
        <button type="button" className="archy-btn" style={toolBtn} onClick={() => downloadVideo()}>
          <DownloadIcon size={15} />
          Download
        </button>
        <button
          type="button"
          className="archy-btn"
          style={toolBtn}
          onClick={screenShot}
          title={projectID ? 'Downloads the frame and saves it to this project’s gallery' : 'Downloads the frame'}
        >
          <CameraIcon size={15} />
          Screenshot
        </button>
        {shotNote && (
          <span role="status" style={{ fontSize: 13, color: ui.muted, whiteSpace: 'nowrap' }}>
            {shotNote}
          </span>
        )}
      </div>
    </ArchyModal>
  );
};
